/**
 * Canadian Cities Helper Functions
 * Utilities for working with Canadian cities data from the Natural Resources Canada Geoname API
 */

/**
 * Fetches a comprehensive list of Canadian cities using the Natural Resources Canada Geoname API
 * @returns {Promise<Array>} A promise that resolves to an array of city objects
 */
// Fallback Canadian cities data in case the API fails
const FALLBACK_CANADIAN_CITIES = [
  { id: 'toronto', name: 'Toronto', province: 'Ontario' },
  { id: 'montreal', name: 'Montreal', province: 'Quebec' },
  { id: 'vancouver', name: 'Vancouver', province: 'British Columbia' },
  { id: 'calgary', name: 'Calgary', province: 'Alberta' },
  { id: 'edmonton', name: 'Edmonton', province: 'Alberta' },
  { id: 'ottawa', name: 'Ottawa', province: 'Ontario' },
  { id: 'winnipeg', name: 'Winnipeg', province: 'Manitoba' },
  { id: 'quebec_city', name: 'Quebec City', province: 'Quebec' },
  { id: 'hamilton', name: 'Hamilton', province: 'Ontario' },
  { id: 'kitchener', name: 'Kitchener', province: 'Ontario' },
  { id: 'london', name: 'London', province: 'Ontario' },
  { id: 'victoria', name: 'Victoria', province: 'British Columbia' },
  { id: 'halifax', name: 'Halifax', province: 'Nova Scotia' },
  { id: 'oshawa', name: 'Oshawa', province: 'Ontario' },
  { id: 'windsor', name: 'Windsor', province: 'Ontario' },
  { id: 'saskatoon', name: 'Saskatoon', province: 'Saskatchewan' },
  { id: 'regina', name: 'Regina', province: 'Saskatchewan' },
  { id: 'st_johns', name: 'St. John\'s', province: 'Newfoundland and Labrador' },
  { id: 'kelowna', name: 'Kelowna', province: 'British Columbia' },
  { id: 'barrie', name: 'Barrie', province: 'Ontario' },
];

export const fetchCanadianCities = async () => {
  console.log('[fetchCanadianCities] Starting API request for Canadian cities');
  try {
    console.log('[fetchCanadianCities] Attempting to fetch data from Geoname API');
    const response = await fetch(
      'https://geogratis.gc.ca/services/geoname/en/geonames.json?concise=CITY&num=5000'
    );
    
    console.log(`[fetchCanadianCities] API response status: ${response.status}`);
    
    if (!response.ok) {
      console.warn(`[fetchCanadianCities] API request failed with status ${response.status}`);
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    console.log('[fetchCanadianCities] Parsing JSON response');
    const data = await response.json();
    console.log(`[fetchCanadianCities] Data received, contains items: ${!!data.items}`);
    
    // Format the cities data into a consistent format
    if (data.items && Array.isArray(data.items)) {
      console.log(`[fetchCanadianCities] Processing ${data.items.length} cities from API`);
      
      // Process the data to ensure all fields are simple primitives (not objects)
      const formattedCities = data.items.map(item => {
        // Handle potential complex objects that could cause React rendering errors
        const id = typeof item.id === 'object' ? String(Math.random()).slice(2) : (item.id || String(Math.random()).slice(2));
        const name = typeof item.name === 'object' ? 'Unknown City' : (item.name || 'Unknown City');
        const province = typeof item.province === 'object' ? '' : (item.province || '');
        
        return {
          id: id,
          name: String(name), // Ensure it's a string
          province: String(province), // Ensure it's a string
          latitude: item.latitude && !isNaN(Number(item.latitude)) ? Number(item.latitude) : null,
          longitude: item.longitude && !isNaN(Number(item.longitude)) ? Number(item.longitude) : null,
          population: item.population && !isNaN(Number(item.population)) ? Number(item.population) : null
        };
      });
      
      console.log(`[fetchCanadianCities] Successfully processed ${formattedCities.length} cities`);
      return formattedCities;
    }
    
    // If no data or wrong format, use fallback
    console.log('[fetchCanadianCities] No valid items in response, using fallback data');
    return FALLBACK_CANADIAN_CITIES;
  } catch (error) {
    console.error('[fetchCanadianCities] Error fetching Canadian cities:', error);
    // Return fallback data on any error
    console.log(`[fetchCanadianCities] Falling back to ${FALLBACK_CANADIAN_CITIES.length} static cities`);
    return FALLBACK_CANADIAN_CITIES;
  }
};

/**
 * Fetches Canadian cities from the specified province using the Geoname Service API
 * @param {string} provinceCode - The province code (e.g., '35' for Ontario)
 * @returns {Promise<Array>} A promise that resolves to an array of city objects
 */
export const fetchCitiesByProvince = async (provinceCode) => {
  console.log(`[fetchCitiesByProvince] Starting request for province code: ${provinceCode}`);
  
  if (!provinceCode) {
    console.log('[fetchCitiesByProvince] No province code provided, returning all fallback cities');
    return FALLBACK_CANADIAN_CITIES;
  }
  
  // Get the province name for fallback filtering
  const provinceObj = PROVINCE_CODES.find(p => p.code === provinceCode);
  const provinceName = provinceObj ? provinceObj.name : '';
  console.log(`[fetchCitiesByProvince] Province name for code ${provinceCode}: ${provinceName || 'Not found'}`);
  
  try {
    // Fetch cities data for the specified province
    console.log(`[fetchCitiesByProvince] Fetching cities for province ${provinceCode}`);
    const response = await fetch(
      `https://geogratis.gc.ca/services/geoname/en/geonames.json?concise=CITY&province=${provinceCode}&num=1000`
    );
    
    console.log(`[fetchCitiesByProvince] API response status: ${response.status}`);
    
    if (!response.ok) {
      console.warn(`[fetchCitiesByProvince] API request failed with status ${response.status}`);
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    console.log('[fetchCitiesByProvince] Parsing JSON response');
    const data = await response.json();
    console.log(`[fetchCitiesByProvince] Data received, contains items: ${!!data.items}`);
    
    if (data.items && Array.isArray(data.items)) {
      console.log(`[fetchCitiesByProvince] Processing ${data.items.length} cities from API for province ${provinceCode}`);
      
      // Process the data to ensure all fields are simple primitives (not objects)
      const formattedCities = data.items.map(item => {
        // Handle potential complex objects that could cause React rendering errors
        const id = typeof item.id === 'object' ? String(Math.random()).slice(2) : (item.id || String(Math.random()).slice(2));
        const name = typeof item.name === 'object' ? 'Unknown City' : (item.name || 'Unknown City');
        const province = typeof item.province === 'object' ? '' : (item.province || '');
        
        return {
          id: id,
          name: String(name), // Ensure it's a string
          province: String(province), // Ensure it's a string
          latitude: item.latitude && !isNaN(Number(item.latitude)) ? Number(item.latitude) : null,
          longitude: item.longitude && !isNaN(Number(item.longitude)) ? Number(item.longitude) : null,
          population: item.population && !isNaN(Number(item.population)) ? Number(item.population) : null
        };
      });
      
      console.log(`[fetchCitiesByProvince] Successfully processed ${formattedCities.length} cities`);
      return formattedCities;
    }
    
    // If API call succeeds but has no data, fall back to filtering
    console.log('[fetchCitiesByProvince] API returned no items, falling back to static data');
    
    if (provinceName) {
      console.log(`[fetchCitiesByProvince] Filtering static data for province: ${provinceName}`);
      const filteredCities = FALLBACK_CANADIAN_CITIES.filter(city => 
        city.province.toLowerCase() === provinceName.toLowerCase()
      );
      console.log(`[fetchCitiesByProvince] Found ${filteredCities.length} cities for ${provinceName} in fallback data`);
      return filteredCities;
    } else {
      console.log('[fetchCitiesByProvince] No province name found for filtering, returning all fallback cities');
      return FALLBACK_CANADIAN_CITIES;
    }
  } catch (error) {
    console.error(`[fetchCitiesByProvince] Error fetching cities for province ${provinceCode}:`, error);
    
    // Fallback to filtering our static list by province
    if (provinceName) {
      console.log(`[fetchCitiesByProvince] Error occurred, filtering fallback data for province: ${provinceName}`);
      const filteredCities = FALLBACK_CANADIAN_CITIES.filter(city => 
        city.province.toLowerCase() === provinceName.toLowerCase()
      );
      console.log(`[fetchCitiesByProvince] Found ${filteredCities.length} cities for ${provinceName} in fallback data`);
      return filteredCities;
    } else {
      // If we can't match the province code, return all cities
      console.log('[fetchCitiesByProvince] Error occurred and no province name found, returning all fallback cities');
      return FALLBACK_CANADIAN_CITIES;
    }
  }
};

/**
 * Searches for Canadian cities by name using the Geoname Service API
 * @param {string} searchTerm - The search term to look for
 * @returns {Promise<Array>} A promise that resolves to an array of matching city objects
 */
export const searchCanadianCities = async (searchTerm) => {
  console.log(`[searchCanadianCities] Starting search with term: '${searchTerm}'`);
  
  if (!searchTerm || searchTerm.trim().length === 0) {
    console.log(`[searchCanadianCities] Empty search term, returning all fallback cities`);
    return FALLBACK_CANADIAN_CITIES;
  }
  
  try {
    const targetUrl = `https://geogratis.gc.ca/services/geoname/en/geonames.json?q=${encodeURIComponent(searchTerm)}*&concise=CITY&num=50`;
    console.log(`[searchCanadianCities] Fetching from API URL: ${targetUrl}`);
    
    const response = await fetch(targetUrl);
    console.log(`[searchCanadianCities] API response status: ${response.status}`);
    
    if (!response.ok) {
      console.warn(`[searchCanadianCities] API request failed with status ${response.status}`);
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    console.log(`[searchCanadianCities] Parsing JSON response`);
    const data = await response.json();
    console.log(`[searchCanadianCities] Data received, contains items: ${!!data.items}`);
    
    if (data.items && Array.isArray(data.items)) {
      console.log(`[searchCanadianCities] Processing ${data.items.length} cities from search results`);
      
      // Process the data to ensure all fields are simple primitives (not objects)
      const formattedCities = data.items.map(item => {
        // Handle potential complex objects that could cause React rendering errors
        const id = typeof item.id === 'object' ? String(Math.random()).slice(2) : (item.id || String(Math.random()).slice(2));
        const name = typeof item.name === 'object' ? 'Unknown City' : (item.name || 'Unknown City');
        const province = typeof item.province === 'object' ? '' : (item.province || '');
        
        return {
          id: id,
          name: String(name), // Ensure it's a string
          province: String(province), // Ensure it's a string
          latitude: item.latitude && !isNaN(Number(item.latitude)) ? Number(item.latitude) : null,
          longitude: item.longitude && !isNaN(Number(item.longitude)) ? Number(item.longitude) : null,
          population: item.population && !isNaN(Number(item.population)) ? Number(item.population) : null
        };
      });
      
      console.log(`[searchCanadianCities] Successfully processed ${formattedCities.length} search results`);
      return formattedCities;
    }
    
    // If API call succeeds but no data, fall back to filtering static list
    console.log(`[searchCanadianCities] API returned no items, falling back to filtering static data`);
    const lowercaseSearchTerm = searchTerm.toLowerCase();
    console.log(`[searchCanadianCities] Filtering static data with term: '${lowercaseSearchTerm}'`);
    
    const filteredCities = FALLBACK_CANADIAN_CITIES.filter(city => 
      city.name.toLowerCase().includes(lowercaseSearchTerm) || 
      city.province.toLowerCase().includes(lowercaseSearchTerm)
    );
    
    console.log(`[searchCanadianCities] Found ${filteredCities.length} cities in fallback data matching search term`);
    return filteredCities;
  } catch (error) {
    console.error(`[searchCanadianCities] Error searching cities for '${searchTerm}':`, error);
    
    // Fall back to filtering our static list
    console.log(`[searchCanadianCities] Error occurred, filtering static data`);
    const lowercaseSearchTerm = searchTerm.toLowerCase();
    
    const filteredCities = FALLBACK_CANADIAN_CITIES.filter(city => 
      city.name.toLowerCase().includes(lowercaseSearchTerm) || 
      city.province.toLowerCase().includes(lowercaseSearchTerm)
    );
    
    console.log(`[searchCanadianCities] Found ${filteredCities.length} cities in fallback data matching search term`);
    return filteredCities;
  }
};

// Export a list of province codes and names for reference
export const PROVINCE_CODES = [
  { code: '10', name: 'Newfoundland and Labrador' },
  { code: '11', name: 'Prince Edward Island' },
  { code: '12', name: 'Nova Scotia' },
  { code: '13', name: 'New Brunswick' },
  { code: '24', name: 'Quebec' },
  { code: '35', name: 'Ontario' },
  { code: '46', name: 'Manitoba' },
  { code: '47', name: 'Saskatchewan' },
  { code: '48', name: 'Alberta' },
  { code: '59', name: 'British Columbia' },
  { code: '60', name: 'Yukon' },
  { code: '61', name: 'Northwest Territories' },
  { code: '62', name: 'Nunavut' }
];